/**
 * Settings → Stop-Loss Plan.
 *
 * Replaces the separate ISL / TSL / Target tabs (2026-11-03). Instead of a store of
 * parameters, the page tells the user, for the choices currently on screen, what will
 * happen to an open position from entry to exit:
 *
 *   · "Your plan at a glance" — a step-by-step plan generated live from the (unsaved)
 *     choices, with a worked example on a price ladder (LONG at 100, stop 95 → R = 5).
 *   · Five numbered groups of controls in the order a trade meets them: initial stop &
 *     target → when the stop counts as hit → trailing → safety net at the broker →
 *     monitoring speed. Each says in plain words what it changes.
 *   · What applies elsewhere: paper trades, option baskets, account-level guards.
 *
 * Every rule described here is the engine's actual rule (trailingSlManager, slConfirm,
 * backupStop, optionBasketMonitor) — keep the two in step.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField, Switch, FormControlLabel, Select, MenuItem,
  FormControl, InputLabel, Button, Chip, Alert, CircularProgress, Collapse, Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import trailService, { TrailSettings } from '../../services/trail.service';
import { settingsService } from '../../services/settings.service';
import { useAppTheme } from '../../hooks/useAppTheme';
import HelpLink from '../common/HelpLink';

const DEFAULTS: TrailSettings = {
  autoTrailActivationMultiplier: 2,
  trailDefaultIntervalForSyncedTrades: 15,
  slBreachHoldMinutes: 2,
  spikeModeEnabled: true,
  spikeTriggerAtrMultiple: 3,
  spikeTriggerWindowMinutes: 60,
  spikeCandleRangeMultiplier: 2.5,
  spikeBreachHoldSeconds: 60,
  profitLockAt2RPercent: 40,
  profitLockAt3RPercent: 60,
  profitLockAt4RPercent: 75,
  trailUseCurrentCandle: true,
  trailBreachBufferType: 'absolute',
  trailBreachBufferValue: 0,
  targetRiskMultiple: 2,
  fastLaneEnabled: true,
  fastLaneIntervalSeconds: 1,
  spikeActivationR: 5,
  slConfirmMode: 'HOLD',
  slCloseTfMinutes: null,
  slEmergencyR: 1,
  brokerBackupStopEnabled: false,
};

const TF_CHOICES: Array<{ v: number | null; label: string }> = [
  { v: null, label: "Each trade's own timeframe (recommended)" },
  { v: 5, label: '5 minutes' }, { v: 15, label: '15 minutes' }, { v: 30, label: '30 minutes' },
  { v: 60, label: '1 hour' }, { v: 120, label: '2 hours' }, { v: 240, label: '4 hours' }, { v: 1440, label: '1 day' },
];
const tfName = (v: number | null | undefined) => (TF_CHOICES.find(t => t.v === (v ?? null))?.label || `${v} min`).replace(' (recommended)', '');
const px = (n: number) => (Math.round(n * 100) / 100).toFixed(2).replace(/\.00$/, '');
const n = (v: any, d: number) => (v == null || v === '' || !Number.isFinite(Number(v)) ? d : Number(v));

// ── Worked example: LONG at 100 with the initial stop at 95 → R = 5. ──
const EX = { entry: 100, stop: 95 };

type Step = { title: string; body: React.ReactNode; tone: 'neutral' | 'bear' | 'bull' | 'warn' | 'dim' };

function bufferText(s: TrailSettings): string {
  const v = n(s.trailBreachBufferValue, 0);
  if (v <= 0) return 'on touch';
  if (s.trailBreachBufferType === 'percent') return `by more than ${v}% of the stop`;
  if (s.trailBreachBufferType === 'ticks') return `by more than ${v} tick${v === 1 ? '' : 's'}`;
  return `by more than ${v} (price units)`;
}

/** The plan, in order, for the settings on screen. */
function buildPlan(s: TrailSettings, activationR: number): { steps: Step[]; levels: Array<{ p: number; label: string; kind: string }> } {
  const R = EX.entry - EX.stop;
  const N = n(s.targetRiskMultiple, 2);
  const target = EX.entry + N * R;
  const candle = s.slConfirmMode === 'CANDLE_CLOSE';
  const eR = n(s.slEmergencyR, 1);
  const emergency = EX.stop - eR * R;
  const trailAt = EX.entry + activationR * R;
  const spikeR = n(s.spikeActivationR, 5);
  const hold = n(s.slBreachHoldMinutes, 2);
  const fast = s.fastLaneEnabled !== false;
  const tf = s.slCloseTfMinutes ?? null;
  const tfTxt = tf == null ? "the trade's own timeframe" : tfName(tf);

  const steps: Step[] = [
    {
      title: 'At entry — the initial stop and target',
      tone: 'neutral',
      body: <>The stop is the level you pick on the chart, or for an alert the low of the previous two CLH candles
        {n(s.trailBreachBufferValue, 0) > 0 ? ' minus the breach buffer' : ''}. Your risk on the trade is called <b>R</b>{' '}
        — here 100 − 95 = <b>5</b>. A target from an alert is entry + {N} × R = <b>{px(target)}</b>.</>,
    },
    candle ? {
      title: `While open — the stop counts on a ${tf == null ? 'candle' : tfName(tf)} close`,
      tone: 'bear',
      body: <>Wicks through 95 are ignored. When a candle of {tfTxt} <b>closes below 95</b>, the position is closed at
        market within about 30 seconds of that close. You get one Telegram heads-up per candle while price sits below
        the stop. <b>Emergency stop:</b> if price falls through <b>{px(emergency)}</b> ({eR} R beyond the stop) and
        stays there for 10 seconds, it closes at once without waiting for the candle — so the worst case is about
        {' '}{px(1 + eR)} R plus slippage. On NSE / MCX, a candle that closes after hours is acted on at the next open.</>,
    } : {
      title: `While open — the stop counts after ${hold} minute${hold === 1 ? '' : 's'} beyond it`,
      tone: 'bear',
      body: <>The app checks the price every 30 seconds{fast ? ' (every second once a breach has started)' : ''}.
        When price goes below 95 {bufferText(s)} and <b>stays below for {hold} minute{hold === 1 ? '' : 's'}</b>, the
        position is closed at market. If price comes back above the stop first, the clock resets. A Telegram message
        tells you when the clock starts.</>,
    },
    {
      title: activationR > 0 ? `Once ${activationR} R in profit — the stop starts trailing` : 'From the start — the stop trails',
      tone: 'bull',
      body: <>{activationR > 0 ? <>When price reaches <b>{px(trailAt)}</b> ({activationR} R), the</> : <>The</>} stop follows
        the Heikin-Ashi swing lows of the trade's timeframe and only ever moves up. The trailed stop is judged by the
        same rule as above ({candle ? 'a candle close, plus the emergency stop' : `${hold} min beyond it`}).
        {s.spikeModeEnabled
          ? <> If a sudden move takes the trade at least <b>{spikeR} R</b> into profit, spike mode tightens it to keep
            {' '}{s.profitLockAt2RPercent}% / {s.profitLockAt3RPercent}% / {s.profitLockAt4RPercent}% of the open profit and confirms a
            breach after {s.spikeBreachHoldSeconds} s instead.</>
          : <> Spike mode is off.</>}</>,
    },
    {
      title: 'Target — closes on touch',
      tone: 'bull',
      body: <>When price touches the target ({px(target)} in the example), the position is closed at once. There is no
        waiting period for a target.</>,
    },
    s.brokerBackupStopEnabled ? {
      title: 'If the app is down — a backup stop rests at Delta',
      tone: 'warn',
      body: <>A reduce-only stop order sits at Delta at <b>{px(emergency)}</b> ({eR} R beyond your stop), triggered by
        Delta&apos;s mark price and moved when your stop moves. It only fires if the app hasn&apos;t closed the position first.
        NSE / MCX positions have no backup stop.</>,
    } : {
      title: 'If the app is down — nothing exits',
      tone: 'dim',
      body: <>Your stops live only in the app; Delta holds no stop order. If the app or its price feed is down, no stop
        fires until it is back. (Backup stop at Delta is OFF — section 4.)</>,
    },
    {
      title: 'Your controls on each trade',
      tone: 'neutral',
      body: <>Drag the stop or target on the chart at any time. On Dashboard → Trades Management you can give one trade a
        different stop mode or candle timeframe, pause trailing, or switch <b>SL Trigger</b> off so a hit only alerts you
        and nothing is closed. SHORT trades are the mirror image.</>,
    },
  ];

  const levels = [
    { p: target, label: `Target ${px(target)} · closes on touch`, kind: 'bull' },
    ...(activationR > 0 && Math.abs(trailAt - target) > 1e-9 ? [{ p: trailAt, label: `${activationR} R ${px(trailAt)} · trailing starts`, kind: 'bullDim' }] : []),
    { p: EX.entry, label: 'Entry 100', kind: 'neutral' },
    { p: EX.stop, label: candle ? `Stop 95 · on a candle close below` : `Stop 95 · ${hold} min below`, kind: 'bear' },
    ...(candle || s.brokerBackupStopEnabled
      ? [{ p: emergency, label: `${px(emergency)} · ${[candle ? 'emergency stop (10 s)' : '', s.brokerBackupStopEnabled ? 'backup stop at Delta' : ''].filter(Boolean).join(' + ')}`, kind: 'bearDash' }]
      : []),
  ];
  return { steps, levels };
}

const PriceLadder: React.FC<{ levels: Array<{ p: number; label: string; kind: string }> }> = ({ levels }) => {
  const { C } = useAppTheme();
  const ps = levels.map(l => l.p);
  const hi = Math.max(...ps), lo = Math.min(...ps);
  const H = 250, top = 16, bot = 16;
  const y = (p: number) => top + (hi - p) / Math.max(1e-9, hi - lo) * (H - top - bot);
  const col = (k: string) => k.startsWith('bull') ? C.bull : k.startsWith('bear') ? C.bear : C.neutral;
  // Keep labels at least 15 px apart.
  const ys: number[] = [];
  for (const l of levels) { let v = y(l.p); if (ys.length && v - ys[ys.length - 1] < 15) v = ys[ys.length - 1] + 15; ys.push(v); }
  return (
    <svg viewBox={`0 0 320 ${H}`} width="100%" style={{ maxWidth: 420, display: 'block' }} role="img" aria-label="Worked example price ladder">
      <line x1={24} x2={24} y1={top} y2={H - bot} stroke={C.dim} strokeWidth={1} />
      {levels.map((l, i) => (
        <g key={i}>
          <line x1={14} x2={300} y1={y(l.p)} y2={y(l.p)} stroke={col(l.kind)} strokeWidth={l.kind === 'neutral' ? 1.5 : 2}
            strokeDasharray={l.kind === 'bearDash' ? '5 4' : l.kind === 'bullDim' ? '2 3' : undefined} opacity={l.kind === 'bullDim' ? 0.8 : 1} />
          <text x={34} y={ys[i] - 4} fontSize={11} fontWeight={600} fill={col(l.kind)}>{l.label}</text>
        </g>
      ))}
    </svg>
  );
};

const StepNo: React.FC<{ n: number }> = ({ n: no }) => {
  const { C } = useAppTheme();
  return <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: C.neutral, color: '#fff', fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{no}</Box>;
};

const Group: React.FC<{ no: number; title: string; what: string; children: React.ReactNode; effect?: React.ReactNode; id?: string }> = ({ no, title, what, children, effect, id }) => (
  <Card variant="outlined" id={id} data-testid={`sl-plan-group-${no}`}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
        <StepNo n={no} />
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{title}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: { xs: 0, sm: 4.75 } }}>{what}</Typography>
      <Box sx={{ ml: { xs: 0, sm: 4.75 } }}>
        {children}
        {effect && (
          <Box sx={{ mt: 2, p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Typography variant="body2"><b>In practice:</b> {effect}</Typography>
          </Box>
        )}
      </Box>
    </CardContent>
  </Card>
);

const ModeCard: React.FC<{ selected: boolean; title: string; text: string; good: string; watch: string; onClick: () => void; testid: string }> = ({ selected, title, text, good, watch, onClick, testid }) => {
  const { C } = useAppTheme();
  return (
    <Box role="radio" aria-checked={selected} tabIndex={0} data-testid={testid}
      onClick={onClick} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      sx={{ p: 1.5, borderRadius: 1.5, cursor: 'pointer', height: '100%', border: '2px solid', borderColor: selected ? C.neutral : 'divider',
        bgcolor: selected ? C.neutralMuted : 'transparent', '&:hover': { borderColor: C.neutral }, '&:focus-visible': { outline: `2px solid ${C.neutral}`, outlineOffset: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${selected ? C.neutral : C.dim}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {selected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.neutral }} />}
        </Box>
        <Typography sx={{ fontWeight: 800 }}>{title}</Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: 1 }}>{text}</Typography>
      <Typography variant="caption" sx={{ display: 'block', color: C.bull }}>✓ {good}</Typography>
      <Typography variant="caption" sx={{ display: 'block', color: C.warn }}>! {watch}</Typography>
    </Box>
  );
};

const StopLossPlanPanel: React.FC = () => {
  const { C } = useAppTheme();
  const [s, setS] = useState<TrailSettings>(DEFAULTS);
  const [activationR, setActivationR] = useState<number>(2);
  const [base, setBase] = useState<{ s: TrailSettings; r: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [spikeOpen, setSpikeOpen] = useState(false);

  useEffect(() => {
    let on = true;
    Promise.all([
      trailService.getSettings().catch(() => null),
      settingsService.getUserSettings().then(r => r?.data?.data?.trailSlActivationR).catch(() => null),
    ]).then(([ts, r]) => {
      if (!on) return;
      const merged = { ...DEFAULTS, ...(ts || {}) } as TrailSettings;
      const ar = r != null ? Number(r) : 2;
      setS(merged); setActivationR(ar); setBase({ s: merged, r: ar });
    });
    return () => { on = false; };
  }, []);

  const set = <K extends keyof TrailSettings>(k: K, v: TrailSettings[K]) => setS(prev => ({ ...prev, [k]: v }));
  const dirty = !!base && (JSON.stringify(base.s) !== JSON.stringify(s) || base.r !== activationR);
  const plan = useMemo(() => buildPlan(s, activationR), [s, activationR]);
  const candle = s.slConfirmMode === 'CANDLE_CLOSE';
  const eR = n(s.slEmergencyR, 1);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const saved = await trailService.saveSettings(s);
      await settingsService.updateUserSettings({ trailSlActivationR: activationR } as any);
      const merged = { ...DEFAULTS, ...saved } as TrailSettings;
      setS(merged); setBase({ s: merged, r: activationR });
      setMsg({ ok: true, text: 'Stop-loss plan saved. It applies to open trades within about 10 seconds.' });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.error?.message || e?.message || 'Save failed' });
    }
    setSaving(false);
  };

  const num = (label: string, k: keyof TrailSettings, opts: { min?: number; max?: number; step?: number; helper?: string; suffix?: string } = {}) => (
    <TextField fullWidth size="small" type="number" label={label}
      value={(s as any)[k] ?? ''} helperText={opts.helper}
      onChange={e => set(k, (e.target.value === '' ? '' : Number(e.target.value)) as any)}
      inputProps={{ min: opts.min, max: opts.max, step: opts.step ?? 1, 'data-testid': `sl-plan-${String(k)}` }} />
  );

  const toneColor = (t: Step['tone']) => t === 'bear' ? C.bear : t === 'bull' ? C.bull : t === 'warn' ? C.warn : t === 'dim' ? C.dim : C.neutral;

  return (
    <Box data-testid="sl-plan" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 9 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Stop-Loss Plan</Typography>
        <Typography variant="body2" color="text.secondary">
          What happens to an open futures / perpetual / stock position, from the moment it opens until it exits. Change a
          choice below and the plan updates before you save. <HelpLink to="risk-management/stop-loss-plan" label="App Guide → The Stop-Loss Plan" />
        </Typography>
      </Box>

      {/* ── The plan at a glance ── */}
      <Card variant="outlined" data-testid="sl-plan-summary" sx={{ borderColor: C.neutralBorder }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem' }}>Your plan at a glance</Typography>
            <Chip size="small" label={candle ? `Stop on a candle close · emergency ${eR} R` : `Stop after ${n(s.slBreachHoldMinutes, 2)} min beyond`} sx={{ fontWeight: 700, bgcolor: C.bearMuted, color: C.bear }} />
            <Chip size="small" label={activationR > 0 ? `Trailing from ${activationR} R` : 'Trailing from the start'} sx={{ fontWeight: 700, bgcolor: C.bullMuted, color: C.bull }} />
            <Chip size="small" label={s.brokerBackupStopEnabled ? 'Backup stop at Delta ON' : 'No backup stop at Delta'} sx={{ fontWeight: 700, bgcolor: s.brokerBackupStopEnabled ? C.warnMuted : C.dimMuted, color: s.brokerBackupStopEnabled ? C.warn : 'text.secondary' }} />
            {dirty && <Chip size="small" label="Unsaved — preview" color="warning" variant="outlined" />}
          </Box>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 7.5 }}>
              <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {plan.steps.map((st, i) => (
                  <Box component="li" key={i} data-testid="sl-plan-step" sx={{ display: 'flex', gap: 1.25, pl: 1.25, borderLeft: `3px solid ${toneColor(st.tone)}` }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>{i + 1}. {st.title}</Typography>
                      <Typography variant="body2" color="text.secondary" component="div">{st.body}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Worked example: LONG at 100, stop 95 (R = 5)
              </Typography>
              <PriceLadder levels={plan.levels} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── 1. Entry ── */}
      <Group no={1} title="Initial stop and target" id="sl-plan-entry"
        what="How the first stop and target are set when a trade opens from an alert. A stop or target you pick on the chart is used as it is."
        effect={<>Alert trades start with a target at entry ± {n(s.targetRiskMultiple, 2)} × R. {n(s.trailBreachBufferValue, 0) > 0
          ? <>The buffer ({bufferText(s).replace('by more than ', '')}) puts the alert&apos;s stop a little further away, and in <i>Price + hold</i> mode price must also pass the stop by that much before the clock starts.</>
          : <>With no buffer, the alert&apos;s stop sits exactly at the CLH low / high.</>}</>}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Breach buffer</InputLabel>
              <Select label="Breach buffer" value={s.trailBreachBufferType || 'absolute'} onChange={e => set('trailBreachBufferType', e.target.value)}>
                <MenuItem value="absolute">Price units</MenuItem>
                <MenuItem value="percent">Percent of the stop</MenuItem>
                <MenuItem value="ticks">Ticks of the instrument</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>{num('Buffer size', 'trailBreachBufferValue', { min: 0, step: 0.01, helper: '0 = no buffer. Capped at 1% of the stop.' })}</Grid>
          <Grid size={{ xs: 12, sm: 4 }}>{num('Target (× R)', 'targetRiskMultiple', { min: 0.5, max: 10, step: 0.1, helper: 'Alert trades: target = entry ± this × R.' })}</Grid>
        </Grid>
      </Group>

      {/* ── 2. When is the stop hit ── */}
      <Group no={2} title="When does the stop count as hit?" id="sl-plan-confirm"
        what="Applies to the initial stop and the trailing stop alike. This is your default; any single trade can use the other mode (Dashboard → Trades Management → the stop-rule chip under SL Trigger)."
        effect={candle
          ? <>A wick through the stop does nothing; a {s.slCloseTfMinutes == null ? "candle of the trade's own timeframe" : tfName(s.slCloseTfMinutes) + ' candle'} closing beyond it exits within ~30 s of the close. The emergency stop {eR} R further out caps a runaway candle (worst case ≈ {px(1 + eR)} R + slippage).</>
          : <>A move beyond the stop has to last {n(s.slBreachHoldMinutes, 2)} min ({s.spikeBreachHoldSeconds} s in spike mode) before the position is closed. A wick shorter than that does nothing; a longer one stops you out even if the candle later closes back above.</>}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ModeCard testid="sl-mode-HOLD" selected={!candle} onClick={() => set('slConfirmMode', 'HOLD')}
              title="Price + hold" text="Exit when price stays beyond the stop for a set time."
              good="Reacts within minutes, whatever the trade's timeframe."
              watch="The same hold on every timeframe; a long wick can stop you out." />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ModeCard testid="sl-mode-CANDLE_CLOSE" selected={candle} onClick={() => set('slConfirmMode', 'CANDLE_CLOSE')}
              title="Candle close" text="Exit only when a candle closes beyond the stop, with an emergency stop further out."
              good="Ignores wicks; matches how strategies are backtested."
              watch="Waits for the close, so a loss can grow up to the emergency stop." />
          </Grid>
        </Grid>
        {candle ? (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 7 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Candle timeframe</InputLabel>
                <Select label="Candle timeframe" value={s.slCloseTfMinutes == null ? 'own' : String(s.slCloseTfMinutes)}
                  inputProps={{ 'data-testid': 'sl-plan-tf' }}
                  onChange={e => set('slCloseTfMinutes', e.target.value === 'own' ? null : Number(e.target.value))}>
                  {TF_CHOICES.map(t => <MenuItem key={String(t.v)} value={t.v == null ? 'own' : String(t.v)}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                A trade&apos;s own timeframe is the chart / alert timeframe it was opened from. A shorter one exits sooner (e.g. 15 min closes on a 4H trade).
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>{num('Emergency stop (R beyond the stop)', 'slEmergencyR', { min: 0.25, max: 5, step: 0.25, helper: 'Acts on price; 10 s confirmation. Default 1.' })}</Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>{num('Hold beyond the stop (minutes)', 'slBreachHoldMinutes', { min: 1, max: 60, helper: 'Price must stay beyond the stop this long. Min 1.' })}</Grid>
            <Grid size={{ xs: 12, sm: 6 }}>{num('Hold in spike mode (seconds)', 'spikeBreachHoldSeconds', { min: 30, max: 300, helper: 'Shorter hold once spike mode is on (section 3). Min 30.' })}</Grid>
          </Grid>
        )}
      </Group>

      {/* ── 3. Trailing ── */}
      <Group no={3} title="Trailing the stop" id="sl-plan-trail"
        what="How the stop follows a winning trade. It only ever tightens; it never moves back. Option baskets are never trailed (see below)."
        effect={<>{activationR > 0 ? `Below ${activationR} R of profit the stop stays where it was set` : 'The stop trails from the moment the trade opens'}; after that it follows the Heikin-Ashi swing {s.trailUseCurrentCandle ? 'including the candle still forming' : 'of completed candles only'}.{s.spikeModeEnabled ? ` A sudden run of ${n(s.spikeActivationR, 5)} R or more switches to the tighter spike lock.` : ''}</>}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth size="small" type="number" label="Start trailing at (R of profit)" value={activationR}
              onChange={e => { const v = parseFloat(e.target.value); if (Number.isFinite(v) && v >= 0) setActivationR(v); }}
              inputProps={{ min: 0, max: 10, step: 0.5, 'data-testid': 'sl-plan-activationR' }}
              helperText="0 = trail from the start. Default 2." />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel control={<Switch checked={!!s.trailUseCurrentCandle} onChange={e => set('trailUseCurrentCandle', e.target.checked)} />}
              label={<Typography variant="body2">Use the candle still forming (follows price faster)</Typography>} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <FormControlLabel control={<Switch checked={!!s.spikeModeEnabled} onChange={e => set('spikeModeEnabled', e.target.checked)} />}
                label={<Typography variant="body2"><b>Spike mode</b> — lock in profit after an exceptional run</Typography>} />
              <Button size="small" onClick={() => setSpikeOpen(o => !o)} endIcon={<ExpandMoreIcon sx={{ transform: spikeOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />}>
                {spikeOpen ? 'Hide details' : 'Details'}
              </Button>
            </Box>
            <Collapse in={spikeOpen && !!s.spikeModeEnabled}>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 12, sm: 4 }}>{num('Only after (R of profit)', 'spikeActivationR', { min: 1, max: 20, step: 0.5, helper: 'Default 5.' })}</Grid>
                <Grid size={{ xs: 12, sm: 4 }}>{num('…and a move of (× ATR)', 'spikeTriggerAtrMultiple', { min: 1, max: 10, step: 0.5, helper: 'Or the candle below. Default 3.' })}</Grid>
                <Grid size={{ xs: 12, sm: 4 }}>{num('…or a candle of (× avg range)', 'spikeCandleRangeMultiplier', { min: 1, max: 10, step: 0.5, helper: 'vs the last 5 candles. Default 2.5.' })}</Grid>
                <Grid size={{ xs: 12, sm: 4 }}>{num('Keep at 2 R (% of profit)', 'profitLockAt2RPercent', { min: 0, max: 100 })}</Grid>
                <Grid size={{ xs: 12, sm: 4 }}>{num('Keep at 3 R (%)', 'profitLockAt3RPercent', { min: 0, max: 100 })}</Grid>
                <Grid size={{ xs: 12, sm: 4 }}>{num('Keep at 4 R (%)', 'profitLockAt4RPercent', { min: 0, max: 100 })}</Grid>
              </Grid>
            </Collapse>
          </Grid>
        </Grid>
      </Group>

      {/* ── 4. Safety net ── */}
      <Group no={4} title="Safety net at the broker (Delta)" id="sl-plan-backup"
        what="Every stop above lives in the app. This optional order sits at Delta so a stop still exists if the app is down."
        effect={s.brokerBackupStopEnabled
          ? <>Each open Delta position gets a reduce-only stop order {eR} R beyond its stop, triggered on Delta&apos;s mark price, moved with your stop and cancelled when the trade closes. If placing it fails you get a Telegram message; your app stop is unaffected.</>
          : <>No order rests at Delta. If the app or its price feed is down, no stop fires until it is back.</>}>
        <FormControlLabel
          control={<Switch checked={!!s.brokerBackupStopEnabled} color="warning" onChange={e => set('brokerBackupStopEnabled', e.target.checked)} data-testid="sl-plan-backup-switch" slotProps={{ input: { 'aria-label': 'Backup stop at Delta' } }} />}
          label={<Typography variant="body2"><b>Place a backup stop order at Delta</b> ({s.brokerBackupStopEnabled ? 'ON' : 'OFF'})</Typography>} />
        <Alert severity={s.brokerBackupStopEnabled ? 'warning' : 'info'} sx={{ mt: 1 }}>
          The backup order is visible in Delta&apos;s order book, which is why it is off by default. It sits {eR} R beyond your
          stop (not at it) and triggers on the mark price, not on a single traded wick. It uses the emergency distance from
          section 2 in both modes.
        </Alert>
      </Group>

      {/* ── 5. Monitoring ── */}
      <Group no={5} title="How closely positions are watched" id="sl-plan-speed"
        what="Every open position is priced every 30 seconds. A position whose stop clock is running, or that is in spike mode, is also checked more often."
        effect={s.fastLaneEnabled !== false
          ? <>While a stop clock is running, the price is checked every {n(s.fastLaneIntervalSeconds, 1)} s, so a stop is closed within about that long after its hold (or the emergency 10 s) runs out.</>
          : <>Positions are only checked every 30 s, so a stop can close up to 30 s after its hold runs out.</>}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel control={<Switch checked={s.fastLaneEnabled !== false} onChange={e => set('fastLaneEnabled', e.target.checked)} />}
              label={<Typography variant="body2">Fast checks while a stop clock is running</Typography>} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{num('Fast check every (seconds)', 'fastLaneIntervalSeconds', { min: 1, max: 10, helper: '1–10. Default 1.' })}</Grid>
        </Grid>
      </Group>

      {/* ── Elsewhere ── */}
      <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem', mt: 1 }}>Where these rules do — and don&apos;t — apply</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ height: '100%' }} data-testid="sl-plan-paper"><CardContent>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Paper chart trades</Typography>
            <Typography variant="body2" color="text.secondary">
              Follow this same plan ({candle ? 'candle close + emergency stop' : `${n(s.slBreachHoldMinutes, 2)} min beyond the stop`}, target on touch) so paper behaves like live.
              Two differences: paper trades are never trailed, and never get a backup stop at Delta.
            </Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ height: '100%' }} data-testid="sl-plan-baskets"><CardContent>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Option baskets — their own rules</Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              Set on each basket when you place it (Options page / Options Workspace), not here:
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                <li>Stop on the premium (default 50% of the entry credit / debit), a ₹ amount, or the underlying&apos;s price.</li>
                <li>Never trailed — the stop moves only when you drag it.</li>
                <li>No stop in the first 5 min; a breach must last 20 s. Target closes at once.</li>
                <li>Values are net of the costs of closing; multi-expiry baskets close 20 min before the nearest expiry.</li>
              </Box>
            </Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ height: '100%' }} data-testid="sl-plan-account"><CardContent>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Account-level guards</Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                <li><b>Daily-loss cap</b> blocks new entries once reached; exits are never blocked (Trading Preferences).</li>
                <li><b>Kill switch</b> blocks new orders only — every stop and target exit still goes through.</li>
                <li><b>Liquidation warnings</b> only warn; they never close a position.</li>
                <li>The <b>🔒 marker</b> (Trading Preferences → Profit Lock Multiplier) is a display flag, not a stop.</li>
              </Box>
            </Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* ── Save bar ── */}
      <Box sx={{ position: 'sticky', bottom: 0, zIndex: 3, mt: 1, py: 1.25, px: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: dirty ? C.warnBorder : 'divider',
        bgcolor: 'background.paper', display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', boxShadow: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, flex: '1 1 220px' }} color={dirty ? C.warn : 'text.secondary'}>
          {dirty ? 'You have unsaved changes — the plan above shows them.' : 'Plan saved.'}
        </Typography>
        {msg && <Typography variant="body2" sx={{ color: msg.ok ? C.bull : C.bear }}>{msg.text}</Typography>}
        <Button size="small" disabled={!dirty || saving} onClick={() => { if (base) { setS(base.s); setActivationR(base.r); setMsg(null); } }}>Discard changes</Button>
        <Button size="small" variant="text" disabled={saving} onClick={() => { setS(prev => ({ ...DEFAULTS, id: prev.id, userId: prev.userId })); setActivationR(2); }}>Restore defaults</Button>
        <Button size="small" variant="contained" data-testid="sl-plan-save" disabled={!dirty || saving} onClick={save}
          startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}>Save plan</Button>
      </Box>
    </Box>
  );
};

export default StopLossPlanPanel;
